import { useCallback, useEffect, useRef, useState } from 'react';
import { getAudioProgress, flushProgressQueue } from '@/utils/audioProgressUtils';
import { useAudioPosition } from './useAudioPosition';
import { AudioPosition } from '@/types/audioPosition';
import {
  buildCompactContext,
  ChapterRuntime,
  findSentenceIndexForOrdinal,
  findSentenceStartOrdinal,
  getSentenceFingerprint,
  resolveTimingOrdinal,
} from '@/utils/ordinalSync';
import { isIOS, VOICE_DISCONNECTED_EVENT } from '@/utils/platform';
import { DEFERRED_PLAYBACK_PRIMER_SRC } from '@/utils/deferredPlaybackPrimer';

const PLAYBACK_UI_UPDATE_MIN_DELTA_SECONDS = 0.25;

interface AudioPlayerSourceOptions {
  /**
   * Somewhere else to read this same audio from: the copy on this device,
   * for a chapter that began as a stream. `audioUrl` stays what it was — it
   * is the chapter's identity here, and everything that resets per chapter
   * keys on it — so moving to the override is a hand-off, not a new
   * chapter: the position and the play state carry across, and nothing
   * else is torn down.
   */
  sourceOverrideUrl?: string | null;
  /**
   * The element has run out of data it expected, or failed outright. A
   * chance for the owner to offer a better source.
   */
  onSourceStarved?: () => void;
}

const useAudioPlayer = (
  audioUrl: string,
  runtime: ChapterRuntime | null,
  bookName: string = 'unknown',
  legacyBookKeys: string[] = [],
  chapterId?: string,
  chapterStartTime: number = 0,
  isFinalChapter: boolean = false,
  { sourceOverrideUrl = null, onSourceStarved }: AudioPlayerSourceOptions = {},
) => {
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [seekValue, setSeekValue] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastPlaybackTimeRef = useRef(0);
  const lastSentenceIndexRef = useRef(-1);
  const seekValueRef = useRef<number | null>(null);
  const isPrimingDeferredPlaybackRef = useRef(false);
  const pendingCanPlayRef = useRef<(() => void) | null>(null);
  // What the element was last given, and for which chapter source. Lets a
  // change of `src` be told apart: a new chapter, or the same chapter read
  // from somewhere else.
  const attachedSourceRef = useRef<{ audioUrl: string; src: string } | null>(null);
  // A hand-off in progress, from the new `src` being set until it has
  // loaded: where to pick up, and whether to keep playing.
  const sourceHandOffRef = useRef<{ resumeAt: number; resumePlaying: boolean } | null>(null);
  // Whether the listener wants audio: set by a call to play and by the
  // element starting, cleared by a call to pause and by the element pausing
  // or ending. Neither `audio.paused` nor the `play` event alone can answer
  // this at a hand-off: a reload pauses the element without a `pause` event,
  // and a play asked for a moment ago has not fired its `play` event yet.
  const wantsPlaybackRef = useRef(false);
  const onSourceStarvedRef = useRef(onSourceStarved);
  onSourceStarvedRef.current = onSourceStarved;
  const sourceUrl = sourceOverrideUrl || audioUrl;

  const clearPendingCanPlay = useCallback(() => {
    const audio = audioRef.current;
    const pending = pendingCanPlayRef.current;
    if (audio && pending) {
      audio.removeEventListener('canplay', pending);
    }
    pendingCanPlayRef.current = null;
  }, []);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsReady(false);
    setSeekValue(null);
    lastSentenceIndexRef.current = -1;
  }, [audioUrl]);

  const getCurrentOrdinal = useCallback((): number => {
    if (!runtime) {
      return -1;
    }
    const seconds = audioRef.current?.currentTime ?? lastPlaybackTimeRef.current;
    return resolveTimingOrdinal(runtime.timings.startsMs, runtime.timings.endsMs, Math.max(0, seconds * 1000));
  }, [runtime]);

  const getCurrentSentenceIndex = useCallback((): number => {
    if (!runtime) {
      return -1;
    }
    const ordinal = getCurrentOrdinal();
    return ordinal < 0 ? -1 : findSentenceIndexForOrdinal(runtime.tokenMap.sentences, ordinal);
  }, [getCurrentOrdinal, runtime]);

  const getCurrentContext = useCallback((wordCount: number = 100): string => {
    if (!runtime) {
      return '';
    }
    return buildCompactContext(runtime.tokenMap, getCurrentOrdinal(), wordCount);
  }, [getCurrentOrdinal, runtime]);

  const getCurrentPosition = useCallback((): AudioPosition => {
    const ordinal = getCurrentOrdinal();
    const timeOffset = audioRef.current?.currentTime ?? lastPlaybackTimeRef.current;
    return {
      sentenceIndex: runtime && ordinal >= 0
        ? findSentenceIndexForOrdinal(runtime.tokenMap.sentences, ordinal)
        : -1,
      textFingerprint: runtime && ordinal >= 0
        ? getSentenceFingerprint(runtime.tokenMap, ordinal)
        : '',
      timeOffset,
      // `timeOffset` is chapter-local. Carry the book-total alongside it, the
      // same way saved positions do, so consumers that report progress
      // (analytics here and on the server) don't read a chapter offset as a
      // position in the book.
      totalProgress: chapterStartTime + timeOffset,
      chapterId,
    };
  }, [chapterId, chapterStartTime, getCurrentOrdinal, runtime]);

  const { savePositionNow, clearSavedPosition, isPositionRestored } = useAudioPosition({
    audioUrl,
    audioRef,
    isReady,
    duration,
    bookName,
    chapterId,
    chapterStartTime,
  });

  const rewindToCurrentSentence = useCallback((): boolean => {
    const audio = audioRef.current;
    const ordinal = getCurrentOrdinal();
    if (!audio || !runtime || ordinal < 0) return false;
    const startOrdinal = findSentenceStartOrdinal(runtime.tokenMap, ordinal);
    const sentenceStart = runtime.timings.startsMs[startOrdinal];
    if (typeof sentenceStart !== 'number') return false;
    audio.currentTime = sentenceStart / 1000;
    lastPlaybackTimeRef.current = audio.currentTime;
    savePositionNow(getCurrentPosition());
    return true;
  }, [getCurrentOrdinal, getCurrentPosition, runtime, savePositionNow]);

  const getLastPlaybackTime = useCallback(
    () => audioRef.current?.currentTime ?? lastPlaybackTimeRef.current,
    [],
  );
  const isPlayerReady = useCallback(() => isReady, [isReady]);

  useEffect(() => {
    seekValueRef.current = seekValue;
  }, [seekValue]);

  const publishPlaybackTime = useCallback((time: number, force: boolean = false) => {
    if (!Number.isFinite(time)) {
      return;
    }
    lastPlaybackTimeRef.current = time;
    setCurrentTime((previous) =>
      force || Math.abs(previous - time) >= PLAYBACK_UI_UPDATE_MIN_DELTA_SECONDS ? time : previous
    );
    const pendingSeek = seekValueRef.current;
    if (pendingSeek !== null && Math.abs(time - pendingSeek) < 0.5) {
      seekValueRef.current = null;
      setSeekValue(null);
    }
  }, []);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }
    clearPendingCanPlay();
    wantsPlaybackRef.current = true;
    const start = () => {
      pendingCanPlayRef.current = null;
      void audio.play().catch((error) => {
        // A hand-off reloads the element, which aborts a play already under
        // way; the hand-off starts it again on the new source.
        if (sourceHandOffRef.current && error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.error('Error playing audio:', error);
      });
    };
    if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      start();
      return;
    }
    pendingCanPlayRef.current = start;
    audio.addEventListener('canplay', start, { once: true });
    if (audio.readyState === HTMLMediaElement.HAVE_NOTHING) {
      audio.load();
    }
  }, [audioUrl, clearPendingCanPlay]);

  const primeForDeferredPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    savePositionNow(getCurrentPosition());
    isPrimingDeferredPlaybackRef.current = true;
    audio.preload = 'auto';
    audio.src = DEFERRED_PLAYBACK_PRIMER_SRC;
    audio.load();
    void audio.play().catch((error) => {
      if (!(error instanceof DOMException) || error.name !== 'AbortError') {
        console.warn('Could not prime audiobook playback:', error);
      }
    });
  }, [getCurrentPosition, savePositionNow]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    clearPendingCanPlay();
    wantsPlaybackRef.current = false;
    if (sourceHandOffRef.current) {
      // Mid hand-off the element is already paused, so no `pause` event is
      // coming to say so; and the new source must not start on its own.
      sourceHandOffRef.current.resumePlaying = false;
      setIsPlaying(false);
    }
    audio.pause();
    savePositionNow(getCurrentPosition());
  }, [clearPendingCanPlay, getCurrentPosition, savePositionNow]);

  const togglePlay = useCallback(() => {
    if (audioRef.current?.paused) {
      play();
    } else {
      pause();
    }
  }, [pause, play]);

  const skipForward = useCallback((secondsOrEvent: number | React.MouseEvent = 10) => {
    const audio = audioRef.current;
    if (!audio || audio.readyState < HTMLMediaElement.HAVE_METADATA) {
      return;
    }
    const seconds = typeof secondsOrEvent === 'number' ? secondsOrEvent : 10;
    if (!Number.isFinite(audio.duration) || !Number.isFinite(audio.currentTime)) {
      return;
    }
    audio.currentTime = Math.min(audio.currentTime + seconds, audio.duration);
    savePositionNow(getCurrentPosition());
  }, [getCurrentPosition, savePositionNow]);

  const skipBackward = useCallback((secondsOrEvent: number | React.MouseEvent = 10) => {
    const audio = audioRef.current;
    if (!audio || audio.readyState < HTMLMediaElement.HAVE_METADATA || !Number.isFinite(audio.currentTime)) {
      return;
    }
    const seconds = typeof secondsOrEvent === 'number' ? secondsOrEvent : 10;
    audio.currentTime = Math.max(audio.currentTime - seconds, 0);
    savePositionNow(getCurrentPosition());
  }, [getCurrentPosition, savePositionNow]);

  // `persist: false` is for seeks that only put the element back where the
  // saved position already says the user is. Writing that value back would
  // re-stamp it with `lastPlayed: Date.now()`, and progress merges
  // last-write-wins on that timestamp — so a restore against a stale local
  // cookie (server hydration not landed yet) would beat the newer position
  // this device hasn't seen. A restore is a read; it must not write.
  const seek = useCallback((time: number, options: { persist?: boolean } = {}) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(time)) {
      return;
    }
    setSeekValue(time);
    seekValueRef.current = time;
    audio.currentTime = time;
    lastPlaybackTimeRef.current = time;
    if (sourceHandOffRef.current) {
      // The new source picks up where the listener has just asked to be.
      sourceHandOffRef.current.resumeAt = time;
    }
    if (options.persist !== false) {
      savePositionNow(getCurrentPosition());
    }
  }, [getCurrentPosition, savePositionNow]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (!audioUrl) {
      clearPendingCanPlay();
      attachedSourceRef.current = null;
      sourceHandOffRef.current = null;
      wantsPlaybackRef.current = false;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      setIsPlaying(false);
      setIsReady(false);
      setDuration(0);
      return;
    }

    isPrimingDeferredPlaybackRef.current = false;
    const normalizedAudioUrl = new URL(sourceUrl, window.location.href).toString();
    const sourceChanged = audio.src !== normalizedAudioUrl;
    // The same chapter, on an element still holding the source it was given
    // for it: only where the audio is read from is changing. (An element
    // holding anything else — the deferred-playback primer — is being given
    // its chapter back, which is the ordinary path below.)
    const attached = attachedSourceRef.current;
    const isHandOff = sourceChanged && attached !== null && attached.audioUrl === audioUrl && audio.src === attached.src;
    if (isHandOff) {
      clearPendingCanPlay();
      const inProgress = sourceHandOffRef.current;
      sourceHandOffRef.current = {
        resumeAt: inProgress?.resumeAt ?? (audio.currentTime || lastPlaybackTimeRef.current || 0),
        resumePlaying: inProgress?.resumePlaying ?? wantsPlaybackRef.current,
      };
      // Setting `src` and loading pauses the element and puts it back at
      // zero, silently. Neither is the listener's doing, so neither is
      // reported: the handlers below hold their tongue until the new
      // source has loaded and playback has been put back where it was.
      audio.src = normalizedAudioUrl;
    } else if (sourceChanged) {
      clearPendingCanPlay();
      sourceHandOffRef.current = null;
      const saved = getAudioProgress(bookName, legacyBookKeys);
      const initialTime = saved && saved.chapterId === chapterId && saved.timeOffset > 0 ? saved.timeOffset : 0;
      setCurrentTime(initialTime);
      lastPlaybackTimeRef.current = initialTime;
      setIsReady(false);
      setIsPlaying(false);
      wantsPlaybackRef.current = false;
      audio.src = normalizedAudioUrl;
      audio.volume = 0.99;
    }
    if (sourceChanged) {
      attachedSourceRef.current = { audioUrl, src: normalizedAudioUrl };
    }

    const completeSourceHandOff = () => {
      const handOff = sourceHandOffRef.current;
      if (!handOff) return;
      sourceHandOffRef.current = null;
      if (Math.abs((audio.currentTime || 0) - handOff.resumeAt) > PLAYBACK_UI_UPDATE_MIN_DELTA_SECONDS) {
        audio.currentTime = handOff.resumeAt;
      }
      lastPlaybackTimeRef.current = handOff.resumeAt;
      if (handOff.resumePlaying) {
        play();
      }
    };
    const handleLoadedMetadata = () => {
      if (isPrimingDeferredPlaybackRef.current) return;
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      setIsReady(true);
      completeSourceHandOff();
    };
    const handleTimeUpdate = () => {
      if (isPrimingDeferredPlaybackRef.current || sourceHandOffRef.current) return;
      publishPlaybackTime(audio.currentTime || 0);
      if (!audio.paused && runtime && isPositionRestored) {
        const sentenceIndex = getCurrentSentenceIndex();
        if (sentenceIndex >= 0 && sentenceIndex !== lastSentenceIndexRef.current) {
          lastSentenceIndexRef.current = sentenceIndex;
          savePositionNow(getCurrentPosition());
        }
      }
    };
    const handlePlay = () => {
      if (isPrimingDeferredPlaybackRef.current) return;
      wantsPlaybackRef.current = true;
      setIsPlaying(true);
      publishPlaybackTime(audio.currentTime || 0, true);
    };
    const handlePause = () => {
      if (isPrimingDeferredPlaybackRef.current || sourceHandOffRef.current) return;
      wantsPlaybackRef.current = false;
      setIsPlaying(false);
      publishPlaybackTime(audio.currentTime || 0, true);
      if (runtime && isPositionRestored && !(isFinalChapter && audio.ended)) {
        savePositionNow(getCurrentPosition());
      }
      void flushProgressQueue();
    };
    const handleEnded = () => {
      if (isPrimingDeferredPlaybackRef.current) return;
      wantsPlaybackRef.current = false;
      setIsPlaying(false);
      publishPlaybackTime(audio.currentTime || 0, true);
    };
    const handleError = () => {
      if (isPrimingDeferredPlaybackRef.current) return;
      if (sourceHandOffRef.current) {
        // The source being handed to is the one that failed; there is
        // nothing further to fall back on.
        sourceHandOffRef.current = null;
        wantsPlaybackRef.current = false;
        setIsPlaying(false);
        setIsReady(false);
        return;
      }
      setIsReady(false);
      onSourceStarvedRef.current?.();
    };
    // `waiting`: playback has stopped for want of data. `stalled`: the
    // element is asking for data and none is arriving. Either way the
    // stream is failing the listener, and the owner may know of a copy on
    // this device.
    const handleStarved = () => {
      if (isPrimingDeferredPlaybackRef.current || sourceHandOffRef.current) return;
      onSourceStarvedRef.current?.();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('waiting', handleStarved);
    audio.addEventListener('stalled', handleStarved);

    if (sourceChanged || audio.readyState === HTMLMediaElement.HAVE_NOTHING) {
      audio.load();
      if (isHandOff && sourceHandOffRef.current) {
        // Before anything has loaded, this sets where playback will start
        // rather than seeking, and it is what `currentTime` then reports —
        // so the highlight and the progress bar, which read the element
        // directly, do not flash back to the start of the chapter.
        try {
          audio.currentTime = sourceHandOffRef.current.resumeAt;
        } catch {
          // Not settable yet on this engine; the seek on load covers it.
        }
      }
    } else if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      handleLoadedMetadata();
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('waiting', handleStarved);
      audio.removeEventListener('stalled', handleStarved);
    };
  }, [
    audioUrl,
    bookName,
    chapterId,
    clearPendingCanPlay,
    getCurrentPosition,
    getCurrentSentenceIndex,
    isFinalChapter,
    isPositionRestored,
    legacyBookKeys,
    play,
    publishPlaybackTime,
    runtime,
    savePositionNow,
    sourceUrl,
  ]);

  useEffect(() => clearPendingCanPlay, [clearPendingCanPlay]);

  useEffect(() => {
    let resumeTimer: number | null = null;
    const handleVoiceDisconnected = () => {
      const audio = audioRef.current;
      if (!isIOS() || !audio || audio.paused) return;
      const resumeTime = audio.currentTime;
      audio.pause();
      resumeTimer = window.setTimeout(() => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = resumeTime;
        void audioRef.current.play().catch((error) =>
          console.error('Error resuming audio after voice disconnect:', error)
        );
      }, 0);
    };
    document.addEventListener(VOICE_DISCONNECTED_EVENT, handleVoiceDisconnected);
    return () => {
      document.removeEventListener(VOICE_DISCONNECTED_EVENT, handleVoiceDisconnected);
      if (resumeTimer !== null) window.clearTimeout(resumeTimer);
    };
  }, []);

  return {
    audioRef,
    isPlaying,
    isReady,
    currentTime: seekValue ?? currentTime,
    duration,
    togglePlay,
    skipForward,
    skipBackward,
    seek,
    getCurrentSentenceIndex,
    getCurrentContext,
    rewindToCurrentSentence,
    getLastPlaybackTime,
    getCurrentPosition,
    isPlayerReady,
    play,
    primeForDeferredPlayback,
    pause,
    clearSavedPosition,
    isPositionRestored,
  };
};

export default useAudioPlayer;
